/**
 * Jubly Email - useEmails Hook
 * Email data fetching and management
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const POLL_INTERVAL = 30000; // 30 seconds

export function useEmails(folder = 'inbox', options = {}) {
  const { 
    page = 1, 
    limit = 50, 
    search = '',
    autoPoll = true 
  } = options;

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const abortControllerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const emailsRef = useRef([]);
  useEffect(() => { emailsRef.current = emails; }, [emails]);

  const fetchEmails = useCallback(async (isPolling = false, offset = 0, append = false) => {
    // Only cancel in-flight requests for a fresh load, not when appending a
    // "load more" page (which must not abort the page it's extending).
    if (!append && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    if (!append) abortControllerRef.current = controller;

    if (!isPolling && !append) setLoading(true);
    if (append) setLoadingMore(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        folder,
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (search) {
        params.append('search', search);
      }

      const response = await fetch(`${API_URL}/emails?${params}`, {
        credentials: 'include',
        signal: controller.signal
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please sign in to view emails');
        }
        throw new Error('Failed to fetch emails');
      }

      const data = await response.json();
      const rows = data.data || data.emails || [];

      setEmails(prev => append ? [...prev, ...rows] : rows);
      setPagination(data.pagination || pagination);
      setHasMore(data.pagination?.hasMore || false);
      setTotal(data.pagination?.total ?? 0); // null during search → no count badge
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      if (!isPolling && !append) setLoading(false);
      if (append) setLoadingMore(false);
    }
  }, [folder, limit, search]);

  // Load the next page and append (infinite scroll).
  const loadMore = useCallback(() => {
    if (hasMore && !loadingMore && !loading) {
      fetchEmails(false, emailsRef.current.length, true);
    }
  }, [hasMore, loadingMore, loading, fetchEmails]);

  // Initial fetch
  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // Auto-polling
  useEffect(() => {
    if (autoPoll) {
      pollIntervalRef.current = setInterval(() => {
        // Only auto-refresh the first page; don't wipe pages the user scrolled in.
        if (emailsRef.current.length <= limit) fetchEmails(true);
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchEmails, autoPoll]);

  const refresh = useCallback(() => {
    return fetchEmails();
  }, [fetchEmails]);

  const markAsRead = useCallback(async (emailId) => {
    try {
      const response = await fetch(`${API_URL}/emails/${emailId}/read`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to mark as read');

      setEmails(prev => prev.map(email =>
        email.id === emailId ? { ...email, is_read: true } : email
      ));

      return true;
    } catch (err) {
      console.error('Mark as read error:', err);
      return false;
    }
  }, []);

  const markAsUnread = useCallback(async (emailId) => {
    try {
      const response = await fetch(`${API_URL}/emails/${emailId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isRead: false }),
      });
      if (!response.ok) throw new Error('Failed to mark as unread');
      setEmails(prev => prev.map(email =>
        email.id === emailId ? { ...email, is_read: false } : email
      ));
      return true;
    } catch (err) {
      console.error('Mark as unread error:', err);
      return false;
    }
  }, []);

  const archiveEmail = useCallback(async (emailId) => {
    try {
      const response = await fetch(`${API_URL}/emails/${emailId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isArchived: true }),
      });

      if (!response.ok) throw new Error('Failed to archive');

      setEmails(prev => prev.filter(email => email.id !== emailId));

      return true;
    } catch (err) {
      console.error('Archive error:', err);
      return false;
    }
  }, []);

  const deleteEmail = useCallback(async (emailId) => {
    try {
      const response = await fetch(`${API_URL}/emails/${emailId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');

      setEmails(prev => prev.filter(email => email.id !== emailId));

      return true;
    } catch (err) {
      console.error('Delete error:', err);
      return false;
    }
  }, []);

  const starEmail = useCallback(async (emailId) => {
    // Toggle star — find current state from emails list
    const current = emails.find(e => e.id === emailId);
    const newStarred = !current?.is_starred;
    try {
      const response = await fetch(`${API_URL}/emails/${emailId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isStarred: newStarred })
      });

      if (!response.ok) throw new Error('Failed to star');

      setEmails(prev => prev.map(email =>
        email.id === emailId ? { ...email, is_starred: newStarred } : email
      ));

      return true;
    } catch (err) {
      console.error('Star error:', err);
      return false;
    }
  }, []);

  return {
    emails,
    loading,
    loadingMore,
    error,
    pagination,
    hasMore,
    total,
    loadMore,
    refresh,
    markAsRead,
    markAsUnread,
    archiveEmail,
    deleteEmail,
    starEmail
  };
}

export function useEmail(emailId) {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!emailId) {
      setEmail(null);
      setLoading(false);
      return;
    }

    const fetchEmail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/emails/${emailId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Email not found');
          }
          throw new Error('Failed to fetch email');
        }

        const data = await response.json();
        setEmail(data.data || data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (emailId) fetchEmail();
  }, [emailId]);

  return { email, loading, error };
}

export function useSendEmail() {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const sendEmail = useCallback(async (emailData) => {
    setSending(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send email');
      }

      const result = await response.json();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSending(false);
    }
  }, []);

  return { sendEmail, sending, error };
}

export function useFolders() {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const response = await fetch(`${API_URL}/folders`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error('Failed to fetch folders');

        const data = await response.json();
        setFolders(data.folders || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, []);

  const createFolder = useCallback(async (name) => {
    try {
      const response = await fetch(`${API_URL}/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });

      if (!response.ok) throw new Error('Failed to create folder');

      const newFolder = await response.json();
      setFolders(prev => [...prev, newFolder]);
      return newFolder;
    } catch (err) {
      console.error('Create folder error:', err);
      throw err;
    }
  }, []);

  return { folders, loading, error, createFolder };
}

export default useEmails;
